import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
    FaUser, FaBars, FaTimes, FaSignOutAlt,
    FaFileInvoice, FaShieldAlt
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";

const PublicNavbar = ({ onOpenEnquiry }) => {
    const { user, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const isAdmin = user?.role === "admin" || user?.role === "superadmin";

    // Nav links without "What We Do"
    const navLinks = [
        { name: "Home", path: "/" },
        { name: "Products", path: "/products" },
        { name: "Applications", path: "/applications" },
        { name: "About Us", path: "/about" },
        { name: "Gallery", path: "/gallery" },
        { name: "Contact", path: "/contact" },
    ];

    const isActive = (path) => {
        if (path === "/" && location.pathname !== "/") return false;
        return location.pathname.startsWith(path);
    };

    // Scroll listener for transparent-to-frosted transition
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 30);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Header background styling: Translucent light white / water-crystal glassmorphism
    const headerClasses = isScrolled
        ? "bg-white/75 backdrop-blur-xl border-b border-slate-200/60 shadow-sm text-slate-800"
        : "bg-white/35 backdrop-blur-xl border-b border-white/35 shadow-[0_4px_20px_0_rgba(0,0,0,0.04)] text-slate-800";

    return (
        <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${headerClasses}`}>
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-14">

                    {/* 1. BRAND LOGO (LEFT) - Frosted water-glass badge for 100% black logo clarity */}
                    <Link to="/" className="flex items-center group flex-shrink-0">
                        <div className="bg-white/70 hover:bg-white/90 backdrop-blur-md px-2.5 py-1 rounded-lg border border-white/60 shadow-xs transition duration-200 flex items-center">
                            <img
                                src="/logo.png"
                                alt="LUMIGO Projection Solutions"
                                className="h-7 w-auto object-contain"
                            />
                        </div>
                    </Link>

                    {/* 2. RIGHT-ALIGNED NAVIGATION & ACTIONS */}
                    <div className="ml-auto hidden lg:flex items-center space-x-6">

                        {/* Navigation Links */}
                        <nav className="flex items-center space-x-6">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className={`text-[13px] font-bold tracking-wide transition-all relative py-1 ${isActive(link.path)
                                        ? "text-[#E3B629] font-extrabold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-[#E3B629]"
                                        : "text-slate-800 hover:text-[#E3B629]"
                                        }`}
                                >
                                    {link.name}
                                </Link>
                            ))}
                        </nav>

                        {/* Get a Quote Button */}
                        <button
                            onClick={() => (onOpenEnquiry ? onOpenEnquiry() : navigate("/contact"))}
                            className="bg-[#E3B629] hover:bg-[#cfa320] text-[#263746] font-extrabold text-[11px] sm:text-xs px-3.5 py-1.5 rounded-lg uppercase tracking-wider transition shadow-xs hover:shadow"
                        >
                            Get a Quote
                        </button>

                        {/* User Account Dropdown (Visible only when authenticated) */}
                        {isAuthenticated && (
                            <div className="relative">
                                <button
                                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                                    className="flex items-center gap-2 p-1 rounded-full hover:bg-slate-100 transition text-slate-700"
                                    aria-label="User Account"
                                >
                                    <div className="w-7 h-7 rounded-full bg-[#E3B629] text-[#263746] flex items-center justify-center font-bold text-xs shadow-xs">
                                        {user?.name?.[0]?.toUpperCase() || <FaUser />}
                                    </div>
                                </button>

                                {userDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 text-xs text-slate-800">
                                        <div className="px-4 py-2 border-b border-slate-100">
                                            <div className="font-bold text-slate-900 truncate">{user?.name}</div>
                                            <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
                                            <span className="inline-block mt-1 px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-50 text-[#b38914]">
                                                {isAdmin ? "Administrator" : "Client Account"}
                                            </span>
                                        </div>

                                        {isAdmin ? (
                                            <Link
                                                to="/admin"
                                                onClick={() => setUserDropdownOpen(false)}
                                                className="flex items-center gap-2 px-4 py-2.5 text-slate-700 hover:text-slate-900 hover:bg-slate-50 font-semibold"
                                            >
                                                <FaShieldAlt className="text-[#E3B629]" /> Admin Dashboard
                                            </Link>
                                        ) : (
                                            <Link
                                                to="/customer"
                                                onClick={() => setUserDropdownOpen(false)}
                                                className="flex items-center gap-2 px-4 py-2.5 text-slate-700 hover:text-slate-900 hover:bg-slate-50 font-semibold"
                                            >
                                                <FaFileInvoice className="text-[#E3B629]" /> My Client Dashboard
                                            </Link>
                                        )}

                                        <button
                                            onClick={() => {
                                                setUserDropdownOpen(false);
                                                logout();
                                            }}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-rose-600 hover:bg-rose-50 text-left border-t border-slate-100 mt-1 font-semibold"
                                        >
                                            <FaSignOutAlt /> Sign Out
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                    </div>

                    {/* 3. MOBILE HAMBURGER BUTTON */}
                    <div className="flex items-center gap-3 lg:hidden">
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="p-2 text-slate-800 hover:bg-slate-100 rounded-lg transition"
                            aria-label="Toggle Navigation"
                        >
                            {mobileMenuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
                        </button>
                    </div>

                </div>
            </div>

            {/* 4. MOBILE MENU DRAWER */}
            {mobileMenuOpen && (
                <div className="lg:hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl px-6 py-6 space-y-4 shadow-2xl animate-fadeIn text-slate-800">
                    <div className="flex flex-col space-y-3">
                        {navLinks.map((link) => (
                            <Link
                                key={link.name}
                                to={link.path}
                                onClick={() => setMobileMenuOpen(false)}
                                className={`text-sm font-semibold py-1.5 transition ${isActive(link.path) ? "text-[#E3B629] font-bold" : "text-slate-700 hover:text-[#E3B629]"
                                    }`}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </div>

                    <div className="pt-4 border-t border-slate-200 flex flex-col gap-2.5">
                        <button
                            onClick={() => {
                                setMobileMenuOpen(false);
                                onOpenEnquiry ? onOpenEnquiry() : navigate("/contact");
                            }}
                            className="w-full bg-[#E3B629] text-[#263746] font-extrabold text-xs py-3 rounded-lg uppercase tracking-wider text-center shadow-sm"
                        >
                            Get a Quote
                        </button>

                        {isAuthenticated && (
                            <div className="flex gap-2">
                                <Link
                                    to={isAdmin ? "/admin" : "/customer"}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex-1 bg-slate-100 text-slate-800 font-bold text-xs py-2.5 rounded-lg text-center hover:bg-slate-200"
                                >
                                    {isAdmin ? "Admin Portal" : "Client Dashboard"}
                                </Link>
                                <button
                                    onClick={() => {
                                        setMobileMenuOpen(false);
                                        logout();
                                    }}
                                    className="px-4 py-2.5 text-xs text-rose-600 border border-rose-200 rounded-lg hover:bg-rose-50 font-bold"
                                >
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};

export default PublicNavbar;
